import React from 'react';
import { useSelector } from 'react-redux';
import {
  Ad,
  AppContent,
  baseUnit,
  ErrorBoundary,
  PrimarySectionTitle,
  Section,
  SectionHeader,
  SectionWithTitle,
  OneOTCThreeHLC,
  ThreeVCThreeHCThreeVCThreeHC,
  DisableMainCategory,
  PaginationButtonGroup,
  NetworkLinkCardFeedPage,
  MEDIA_BREAKPOINTS,
  LARGE_SCREEN_SIZE,
} from 'mm-ui-components';
import { Store } from 'redux';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Theme } from '../../components/connectedComponents/Theme';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { SECTION_NAMES, SLOT_NAMES } from '../templates.const';
import { LoadMoreButton, useFetchNextPosts } from '../../components/connectedComponents/LoadMoreButton';
import { Page } from '../Page';
import { getAdSlotId, getAdSlotOnSuccess } from '../../store/config/config.selectors';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import { NavigationLayout } from '../../components/connectedComponents/NavigationLayout';
import {
  getMetadataDescription,
  getMetadataTitle,
  getSectionArticlesByKey,
  getSectionSubtitleByKey,
  getSectionTitleByKey,
  getShowMorePaginationTextByKey,
  getShowMorePaginationURLByKey,
  getMetadataImage,
  getHreflangData,
  getPagination,
  getNetworkLink,
  getSportradarProps,
} from '../../store/template/feedPage/feedPageRome/feedPageRome.selectors';
import { getPageMetaText } from '../templates.utils';
import { FeedPageRomeSRHeaderWidget, FeedPageRomeSRBodyWidgets } from './FeedPageRomeSRWidgets';

interface FeedPageRomeProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

const getArticlesInTemplate = (state: AppState) => {
  const topSectionArticles = getSectionArticlesByKey(SECTION_NAMES.TOP_SECTION)(state) || [];
  const feedSectionArticles = getSectionArticlesByKey(SECTION_NAMES.FEED_SECTION)(state) || [];
  return [
    ...topSectionArticles,
    ...feedSectionArticles,
  ];
};

export const FeedPageRomeComponent: React.FunctionComponent<FeedPageRomeProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <FeedPageRomeContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

const createStyle = () => StyleSheet.create({
  sectionsWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridRowGap: `${baseUnit * 0.5}px`,
    marginTop: `${baseUnit * 1.5}px`,
    marginBottom: `${baseUnit * 0.5}px`,
    justifyItems: 'center',
  },
  contentWithWidgets: {
    maxWidth: `${LARGE_SCREEN_SIZE}px`,
    margin: 'auto',

    [MEDIA_BREAKPOINTS.large]: {
      display: 'grid',
      gridTemplateColumns: '2fr 1fr',
      columnGap: '20px',
      gridTemplateAreas: '"second first"',
      alignItems: 'flex-start',
    },
  },
  widgets: {
    gridArea: 'first',
    alignSelf: 'flex-start',
  },
  content: {
    gridArea: 'second',
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridRowGap: `${baseUnit * 0.5}px`,
    justifyItems: 'center',
    alignSelf: 'flex-start',
  },
});

export const FeedPageRomeContentConnected: React.FunctionComponent = () => {
  const topSectionDisplayName = useSelector(getSectionTitleByKey(SECTION_NAMES.TOP_SECTION));
  const topSectionSubtitleName = useSelector(getSectionSubtitleByKey(SECTION_NAMES.TOP_SECTION));
  const topSectionArticles = useSelector(getSectionArticlesByKey(SECTION_NAMES.TOP_SECTION));
  const articles = useSelector(getArticlesInTemplate);
  const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
  const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
  const stickyBottomId = useSelector(getAdSlotId(SLOT_NAMES.STICKY_BOTTOM));
  const belowTopSectionAdId = useSelector(getAdSlotId(SLOT_NAMES.BELOW_TOP_SECTION));
  const topAdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.TOP_AD));
  const oneOnOneOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.ONE_ON_ONE));
  const stickyBottomOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.STICKY_BOTTOM));
  const belowTopSectionOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.BELOW_TOP_SECTION));
  const metaTitle = useSelector(getMetadataTitle);
  const metaDescription = useSelector(getMetadataDescription);
  const pageImage = useSelector(getMetadataImage);
  const hreflangData = useSelector(getHreflangData) || [];
  const paginationData = useSelector(getPagination);
  const sportradarProps = useSelector(getSportradarProps);
  const shouldShowSRWidgets = !!(sportradarProps?.teamUid && sportradarProps?.league && sportradarProps?.seasonId);
  const { currentPage, prevButtonText, prevButtonURL, nextButtonText, nextButtonURL } = paginationData;

  const style = createStyle();

  return (
    <React.Fragment>
      <MetaTags {...getPageMetaText(metaTitle, metaDescription, currentPage)} articles={articles} pageImage={pageImage} alternateUrls={hreflangData} />
      <HeadScripts />
      <Theme>
        <DisableMainCategory>
          <AppContent>
            <NavigationLayout
              mainWrapperConfigThemeKey="mainContent"
              isFullBleed={shouldShowSRWidgets}
            >
              {shouldShowSRWidgets && <FeedPageRomeSRHeaderWidget sportradarProps={sportradarProps} />}
              <Ad id={topAdId} onSuccess={topAdOnSuccess} setMinHeight />
              <div className={css(style.sectionsWrapper)}>

                <SectionWithTitle>
                  <SectionHeader moreButtonSvg={null} moreButtonText={null} description={topSectionSubtitleName}>
                    <PrimarySectionTitle>{topSectionDisplayName}</PrimarySectionTitle>
                  </SectionHeader>
                  <OneOTCThreeHLC cards={topSectionArticles} />
                </SectionWithTitle>
                <Ad id={belowTopSectionAdId} onSuccess={belowTopSectionOnSuccess} setMinHeight />
                {shouldShowSRWidgets ? (
                  <div className={css(style.contentWithWidgets)}>
                    <FeedPageRomeSRBodyWidgets
                      sportradarProps={sportradarProps}
                      additionalStyle={style.widgets}
                    />
                    <div className={css(style.content)}>
                      <MainContent />
                    </div>
                  </div>
                ) : (
                  <MainContent />
                )}
              </div>
              {Object.keys(paginationData).length === 0
                ? <LoadMoreFeedPostsButtonConnected />
                : <PaginationButtonGroup prevText={prevButtonText} prevURL={prevButtonURL} nextText={nextButtonText} nextURL={nextButtonURL} />}
              <Ad id={oneOnOneId} onSuccess={oneOnOneOnSuccess} />
            </NavigationLayout>
            <Ad id={stickyBottomId} onSuccess={stickyBottomOnSuccess} isSticky />
          </AppContent>
        </DisableMainCategory>
      </Theme>
    </React.Fragment>
  );
};


const MainContent: React.FunctionComponent = () => {
  const cardsSectionArticles = useSelector(getSectionArticlesByKey(SECTION_NAMES.FEED_SECTION));
  const paginationData = useSelector(getPagination);
  const networkLinks = useSelector(getNetworkLink);
  const isFirstPage = paginationData?.currentPage === 1;

  return (
    <>
      <Section>
        <ThreeVCThreeHCThreeVCThreeHC cards={cardsSectionArticles} />
      </Section>
      {isFirstPage && networkLinks && <Section><NetworkLinkCardFeedPage {...networkLinks} /></Section>}
    </>
  );
};

export const LoadMoreFeedPostsButtonConnected: React.FunctionComponent = () => {
  const sectionKey = SECTION_NAMES.FEED_SECTION;
  const shouldDisplay = !!useSelector(getShowMorePaginationURLByKey(sectionKey));
  const text = useSelector(getShowMorePaginationTextByKey(sectionKey));
  const onClick = useFetchNextPosts(sectionKey, getShowMorePaginationURLByKey);
  return <LoadMoreButton {...{ onClick, shouldDisplay, text }} />;
};
